import React from "react";
import MuiIconButton from "@mui/material/IconButton";
import PropTypes from "prop-types";

/**
 * IconButton component wrapper for icon-based actions.
 * Wraps Material-UI IconButton with theme integration.
 * 
 * @component
 * @example
 * <IconButton color="primary" onClick={handleClick}>
 *   <DeleteIcon />
 * </IconButton>
 */
const IconButton = ({ children, ...props }) => {
  return <MuiIconButton {...props}>{children}</MuiIconButton>;
};

IconButton.propTypes = {
  /**
   * The icon element
   */
  children: PropTypes.node,
  /**
   * The color of the component
   */
  color: PropTypes.oneOf([
    "inherit",
    "default",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ]),
  /**
   * The size of the component
   */
  size: PropTypes.oneOf(["small", "medium", "large"]),
  /**
   * If true, the button will be disabled
   */
  disabled: PropTypes.bool,
  /**
   * If true, the button will have no background or border
   */
  disableRipple: PropTypes.bool,
  /**
   * If given, uses a negative margin to counteract the padding on one side
   */
  edge: PropTypes.oneOf(["start", "end", false]),
  /**
   * Callback fired when the button is clicked
   */
  onClick: PropTypes.func,
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * ARIA label for accessibility
   */
  "aria-label": PropTypes.string,
};

// No defaultProps - maintain exact MUI IconButton behavior
// MUI IconButton defaults: color="default", size="medium"

export default IconButton;

